import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./lightboxV2.html";
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import template from './lightboxV2.html';
const FOCUS_SHIM_INDEX = 0;
const FOCUS_ELEMENTS_SELECTOR = 'iframe, video, button, [tabindex]:not([tabindex="-1"]), a[href]:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';
class LightBoxV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.i18nModule = void 0;
    this.labels = void 0;
    this._items = [];
    this._initialItemPosition = 0;
    this.ga4EventType = '';
    this._currentItem = void 0;
    /** False = poster+play; true = iframe/video tag is mounted and playing. */
    this.isVideoPlaying = false;
    this._initialRender = true;
    this._isDraggingScrollbar = false;
    this._scrollbarStartX = 0;
    this._scrollbarStartLeft = 0;
    this._hiddenHeaderEls = new Map();
    this._raisedRootContainers = new Map();
    this._bodyStyleAttribute = null;
    this._bodyHadOpenClass = false;
    this.scrollbarValue = 0;
    this.scrollbarTabIndex = -1;
  }
  get items() {
    return this._items;
  }
  set items(value) {
    const incoming = value || [];
    // Dedupe by src so wrap-around never paints the first slide again after the last
    const seen = new Set();
    const deduped = [];
    for (const item of incoming) {
      const id = item.src || item.thumb || `position:${item.position}`;
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      deduped.push(item);
    }
    this._items = deduped.map((item, position) => ({
      ...item,
      position
    }));
    if (!this._items.length) {
      return;
    }
    // Keep the same slide across item mutations (e.g. removeAutoplay); otherwise apply initial.
    if (this._currentItem) {
      const src = this._currentItem.src;
      this._currentItem = this._items.find(item => item.src === src) ?? this._items.find(item => item.position === this._currentItem.position) ?? this._items[0];
    } else {
      this.applyInitialItem();
    }
  }

  /**
   * Parent templates may set `items` before this attribute. Use a setter so a late
   * initial position still wins (otherwise the gallery always opens on slide 0).
   */
  get initialItemPosition() {
    return this._initialItemPosition;
  }
  set initialItemPosition(value) {
    const next = Number(value);
    this._initialItemPosition = Number.isFinite(next) ? next : 0;
    if (this._items.length > 0) {
      this.applyInitialItem();
    }
  }
  render() {
    return template;
  }
  get thumbnailItems() {
    const current = this._currentItem?.position;
    const total = this._items.length;
    const carousel = i18nStore.getState().i18n.listingDetails.carousel;
    return this._items.map((item, arrayIndex) => {
      const isSelected = current === item.position;
      const classes = ['lightbox-thumbnail'];
      if (isSelected) {
        classes.push('selected');
      }
      if (item.isVideo) {
        classes.push('is-video');
      }
      return {
        position: item.position,
        thumb: item.thumb,
        isVideo: item.isVideo,
        arrayIndex,
        isSelected,
        // Bound string — LWC does not interpolate inside HTML attribute literals
        ariaLabel: carousel.pagination({
          CURRENT_POSITION: item.position + 1,
          TOTAL_ITEMS: total
        }),
        thumbnailClass: classes.join(' ')
      };
    });
  }
  get src() {
    return this._currentItem?.src || '';
  }
  get caption() {
    return this._currentItem?.caption || '';
  }
  get altText() {
    return this._currentItem?.altText || this.caption;
  }
  get showVideo() {
    return this._currentItem?.type === 'Video';
  }
  get shouldAutoplay() {
    return this._currentItem?.shouldAutoPlay || false;
  }
  get thumb() {
    return this._currentItem?.thumb || '';
  }
  get playVideoLabel() {
    const carousel = i18nStore.getState().i18n.listingDetails.carousel;
    if (this.caption) {
      return carousel.lightboxPlayLabel({
        CAPTION: this.caption
      });
    }
    return carousel.lightboxPlayFallback();
  }
  get currentPosition() {
    return (this._currentItem?.position ?? 0) + 1;
  }
  get totalItems() {
    return this._items.length;
  }

  /** Match ace/lightbox: no prev/next when there is only one slide. */
  get enableControls() {
    return this._items.length > 1;
  }
  get containerClass() {
    return this.enableControls ? 'container has-controls' : 'container';
  }
  applyInitialItem() {
    if (!this._items.length) {
      return;
    }
    this._currentItem = this._items.find(item => item.position === this._initialItemPosition) ?? this._items[0];
    this.isVideoPlaying = false;
  }
  setCurrentItem(position) {
    const foundItem = this.items.find(item => item.position === position);
    if (foundItem) {
      this._currentItem = foundItem;
      this.isVideoPlaying = false;
      this.screenshotEvent();
      this.syncScrollbarAfterLayout();
      this.scrollSelectedThumbnailIntoView();
    }
  }

  /**
   * Swap the poster for the embed/native player and start playback in-lightbox.
   */
  handlePlayVideo() {
    if (!this.showVideo || !this._currentItem) {
      return;
    }
    let src = this._currentItem.src || '';
    if (src && !/[?&]autoplay=\d/.test(src)) {
      src = src.includes('?') ? `${src}&autoplay=1` : `${src}?autoplay=1`;
    }
    this._currentItem = {
      ...this._currentItem,
      src,
      shouldAutoPlay: true
    };
    this.isVideoPlaying = true;
    this._focusElement = 'iframe.video';
    this.dispatchGA4Event('play');
  }
  showNextItem() {
    const currentItemPosition = this._currentItem.position;
    const newPosition = this.isLastItem() ? 0 : currentItemPosition + 1;
    this.removeAutoplay();
    this.setCurrentItem(newPosition);
    this.dispatchGA4Event('next');
  }
  showPreviousItem() {
    const currentItemPosition = this._currentItem.position;
    const newPosition = this.isFirstItem() ? this.lastItemPosition() : currentItemPosition - 1;
    this.removeAutoplay();
    this.setCurrentItem(newPosition);
    this.dispatchGA4Event('back');
  }
  selectThumbnail(event) {
    const button = event.currentTarget;
    const index = button.getAttribute('data-index');
    if (index !== null) {
      const arrayIndex = parseInt(index, 10);
      this.removeAutoplay();
      // Pass the position, not the array index
      this.setCurrentItem(this.items[arrayIndex]?.position ?? 0);
      this.dispatchGA4Event('thumbnail');
    }
  }
  removeAutoplay() {
    // eslint-disable-next-line @lwc/lwc/no-api-reassignments
    this.items = this.items.map(item => {
      let src = item.src;
      try {
        // URL API keeps sibling params (e.g. start=) — bare regex turns ?autoplay=1&start=N into &start=N
        const url = new URL(src);
        url.searchParams.delete('autoplay');
        src = url.toString();
      } catch {
        // Non-absolute src: leave unchanged rather than corrupt the query string
      }
      return {
        ...item,
        src,
        shouldAutoPlay: false
      };
    });
  }
  isFirstItem() {
    return this._currentItem.position === 0;
  }
  lastItemPosition() {
    return this.items.length - 1;
  }
  isLastItem() {
    return this._currentItem.position + 1 > this.items.length - 1;
  }
  handleTab(event) {
    this._previousFocusedElement = event.target;
    if (event.shiftKey) {
      if (event.target === this._firstFocusableElement) {
        this._lastFocusableElement.focus();
        event.preventDefault();
      }
    } else {
      if (event.target === this._lastFocusableElement) {
        this._firstFocusableElement.focus();
        event.preventDefault();
      }
    }
  }
  connectedCallback() {
    if (this._items.length > 0 && !this._currentItem) {
      this.applyInitialItem();
    }
    // Host attribute drives :host([data-gallery]) fixed layer (inner div attribute does not)
    this.setAttribute('data-gallery', '');
    this._bodyStyleAttribute = document.body.getAttribute('style');
    this._bodyHadOpenClass = document.body.classList.contains('lightbox-v2-open');
    document.body.style.setProperty('overflow-y', 'hidden');

    // Hide sticky marketplace header (z-index 1000) via shadow deep-walk
    document.body.classList.add('lightbox-v2-open');
    this.hidePageHeaders();

    // Dialog onkeydown only fires when focus is inside the lightbox. After a carousel
    // click, focus often stays on the trigger — catch Escape on document when outside.
    this._documentKeyDownHandler = e => this.handleDocumentKeyDown(e);
    document.addEventListener('keydown', this._documentKeyDownHandler);
  }
  disconnectedCallback() {
    if (this._mediaResizeObserver) {
      this._mediaResizeObserver.disconnect();
      this._mediaResizeObserver = undefined;
    }
    if (this._documentMouseMoveHandler) {
      document.removeEventListener('mousemove', this._documentMouseMoveHandler);
      this._documentMouseMoveHandler = undefined;
    }
    if (this._documentMouseUpHandler) {
      document.removeEventListener('mouseup', this._documentMouseUpHandler);
      this._documentMouseUpHandler = undefined;
    }
    if (this._documentKeyDownHandler) {
      document.removeEventListener('keydown', this._documentKeyDownHandler);
      this._documentKeyDownHandler = undefined;
    }
    if (this._bodyStyleAttribute === null) {
      document.body.removeAttribute('style');
    } else {
      document.body.setAttribute('style', this._bodyStyleAttribute);
    }
    if (!this._bodyHadOpenClass) {
      document.body.classList.remove('lightbox-v2-open');
    }
    this.restorePageHeaders();
    this.removeAttribute('data-gallery');
  }

  /**
   * Headers live inside page/container shadow — document.querySelector cannot see them.
   * Deep-walk known page/header shells and hide header hosts with inline !important
   * so the sticky marketplace header (z-index 1000) cannot sit above the gallery.
   */
  hidePageHeaders() {
    this._hiddenHeaderEls.clear();
    this._raisedRootContainers.clear();
    const headerSelector = 'ace-header-wrapper, ace-header, ax-marketplace-header';
    const hide = el => {
      if (!(el instanceof HTMLElement) || this._hiddenHeaderEls.has(el)) {
        return;
      }
      this._hiddenHeaderEls.set(el, this.captureInlineStyles(el, ['display', 'visibility', 'pointer-events']));
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
    };
    const raiseRoot = el => {
      if (!(el instanceof HTMLElement) || this._raisedRootContainers.has(el)) {
        return;
      }
      this._raisedRootContainers.set(el, this.captureInlineStyles(el, ['z-index']));
      el.style.setProperty('z-index', '100000', 'important');
    };
    const visit = root => {
      root.querySelectorAll(headerSelector).forEach(hide);
      root.querySelectorAll('.root-container').forEach(raiseRoot);
      // Descend only into known page/header shells (avoid full-tree querySelectorAll('*'))
      root.querySelectorAll('page-container, analytics-handler, ace-header-wrapper, ace-header').forEach(el => {
        if (el.shadowRoot) {
          visit(el.shadowRoot);
        }
      });
    };
    visit(document);
  }
  captureInlineStyles(element, properties) {
    return Object.fromEntries(properties.map(property => [property, {
      value: element.style.getPropertyValue(property),
      priority: element.style.getPropertyPriority(property)
    }]));
  }
  restoreInlineStyles(element, snapshot) {
    Object.entries(snapshot).forEach(([property, {
      value,
      priority
    }]) => {
      if (value) {
        element.style.setProperty(property, value, priority);
      } else {
        element.style.removeProperty(property);
      }
    });
  }
  restorePageHeaders() {
    this._hiddenHeaderEls.forEach((snapshot, element) => {
      this.restoreInlineStyles(element, snapshot);
    });
    this._hiddenHeaderEls.clear();
    this._raisedRootContainers.forEach((snapshot, element) => {
      this.restoreInlineStyles(element, snapshot);
    });
    this._raisedRootContainers.clear();
  }
  close() {
    this.dispatchEvent(new CustomEvent('close'));
    this.dispatchGA4Event('close');
  }
  renderedCallback() {
    if (this._initialRender) {
      this._initialRender = false;
      this.screenshotEvent();

      // Cache scrollbar elements
      this._thumbScrollContainer = this.template.querySelector('.lightbox-thumbnails');
      this._thumbScrollbar = this.template.querySelector('.lightbox-thumbs-scrollbar');
      this._thumbScrollbarThumb = this.template.querySelector('.lightbox-thumbs-scrollbar__thumb');
      this.observeMediaFrame();
      this.syncScrollbarAfterLayout();
      this.scrollSelectedThumbnailIntoView();

      // Wire up document-level drag listeners for scrollbar (removed in disconnectedCallback)
      this._documentMouseMoveHandler = e => this.handleThumbDrag(e);
      this._documentMouseUpHandler = () => this.endThumbScroll();
      document.addEventListener('mousemove', this._documentMouseMoveHandler);
      document.addEventListener('mouseup', this._documentMouseUpHandler);

      // One-shot focus into the dialog on open (cleared below so later re-renders do not steal focus)
      if (!this._focusElement) {
        this._focusElement = '.close__button';
      }
    }
    const focusableContent = this.getFocusableElements();
    if (focusableContent.length > 0) {
      this._firstFocusableElement = focusableContent[0];
      this._lastFocusableElement = focusableContent[focusableContent.length - 1];
    }
    if (this._focusElement) {
      this.forceFocus(this._focusElement);
      this._focusElement = '';
    }
  }
  handleDocumentKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }
    // Dialog onkeydown already handles Escape when focus is inside (composed bubble).
    // Skip so carousel v1 toggleLightbox does not get a double close→reopen.
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(this)) {
      return;
    }
    event.preventDefault();
    this.close();
  }
  observeMediaFrame() {
    const mediaFrame = this.template.querySelector('.media-frame');
    if (!mediaFrame || this._mediaResizeObserver || typeof ResizeObserver === 'undefined') {
      return;
    }
    this._mediaResizeObserver = new ResizeObserver(() => {
      if (!this.isConnected) {
        return;
      }
      this.updateScrollbarDimensions();
      this.updateThumbnailScroll();
    });
    this._mediaResizeObserver.observe(mediaFrame);
  }

  /** Wait for layout (ax-image / 16:9 frame) before measuring scrollbar track width. */
  syncScrollbarAfterLayout() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) {
          return;
        }
        this.updateScrollbarDimensions();
        this.updateThumbnailScroll();
      });
    });
  }
  screenshotEvent() {
    const ev = new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        elementType: 'image',
        eventType: 'imageShown',
        image: {
          src: this.src,
          positionIndex: this.currentPosition
        }
      }
    });
    this.dispatchEvent(ev);
  }
  handleKeyDown(event) {
    switch (event.key) {
      case 'Tab':
        this.handleTab(event);
        break;
      case 'Escape':
        event.stopPropagation();
        this.close();
        break;
      case 'ArrowLeft':
        this._focusElement = '.back__button';
        this.showPreviousItem();
        break;
      case 'ArrowRight':
        this._focusElement = '.forward__button';
        this.showNextItem();
        break;
      default:
        break;
    }
  }
  forceFocus(element) {
    this.template.querySelector(element)?.focus();
  }
  handleModalInteractionLoopFocus(event) {
    const focusableContent = this.getFocusableElements();
    if (focusableContent.length > 1) {
      const firstNonShimItem = focusableContent[FOCUS_SHIM_INDEX + 1];
      const lastItem = focusableContent[focusableContent.length - 1];
      if (this._previousFocusedElement === this._lastFocusableElement) {
        firstNonShimItem.focus();
        event.preventDefault();
      } else if (this._previousFocusedElement === firstNonShimItem) {
        lastItem.focus();
        event.preventDefault();
      }
    }
  }
  getFocusableElements() {
    const modal = this.template.querySelector('[role=dialog]');
    if (!modal) {
      return [];
    }
    return Array.from(modal.querySelectorAll(FOCUS_ELEMENTS_SELECTOR));
  }
  dispatchGA4Event(label) {
    if (this.ga4EventType) {
      const eventDetail = {
        elementType: this.ga4EventType,
        eventType: 'click',
        label: label
      };
      const ev = createEvent(eventDetail);
      this.dispatchEvent(ev);
    }
  }

  /**
   * Sync scrollbar grabber to the compact CSS track + thumbnail scroll range.
   * Track width is CSS-owned (short centered bar) — never stretch to media-frame.
   */
  updateScrollbarDimensions() {
    if (!this._thumbScrollbar || !this._thumbScrollbarThumb || !this._thumbScrollContainer) {
      return;
    }

    // Measure without the overflow spacer so ::after doesn't inflate scrollWidth
    this._thumbScrollContainer.classList.remove('is-overflowing');
    const scrollWidth = this._thumbScrollContainer.scrollWidth;
    const containerWidth = this._thumbScrollContainer.offsetWidth;
    if (scrollWidth <= 0 || containerWidth <= 0) {
      return;
    }
    const needsScroll = scrollWidth > containerWidth + 1;
    this.scrollbarTabIndex = needsScroll ? 0 : -1;
    this._thumbScrollbar.classList.toggle('is-hidden', !needsScroll);
    this._thumbScrollContainer.classList.toggle('is-centered', !needsScroll);
    this._thumbScrollContainer.classList.toggle('is-overflowing', needsScroll);
    if (!needsScroll) {
      return;
    }
    const ratio = Math.min(1, containerWidth / scrollWidth);
    const trackWidth = this._thumbScrollbar.offsetWidth;
    const thumbWidth = Math.max(1.5 * 16, Math.ceil(trackWidth * ratio));
    this._thumbScrollbarThumb.style.width = `${thumbWidth}px`;
  }

  /**
   * Sync scrollbar thumb position to thumbnail scroll position
   */
  updateThumbnailScroll() {
    if (!this._thumbScrollContainer || !this._thumbScrollbarThumb) {
      return;
    }
    const scrollLeft = this._thumbScrollContainer.scrollLeft;
    const scrollWidth = this._thumbScrollContainer.scrollWidth;
    const containerWidth = this._thumbScrollContainer.offsetWidth;
    const scrollRange = scrollWidth - containerWidth;
    const trackWidth = this._thumbScrollbar?.offsetWidth || containerWidth;
    const thumbWidth = this._thumbScrollbarThumb.offsetWidth;
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const ratio = scrollRange > 0 ? scrollLeft / scrollRange : 0;
    this.scrollbarValue = Math.round(ratio * 100);
    const thumbLeft = ratio * maxThumbLeft;
    this._thumbScrollbarThumb.style.left = `${thumbLeft}px`;
  }

  /** Keep the active thumb visible when opening or changing slides. */
  scrollSelectedThumbnailIntoView() {
    requestAnimationFrame(() => {
      if (!this.isConnected || !this._thumbScrollContainer) {
        return;
      }
      const selected = this.template.querySelector('.lightbox-thumbnail.selected');
      if (!selected) {
        return;
      }
      const container = this._thumbScrollContainer;
      const selectedLeft = selected.offsetLeft;
      const selectedRight = selectedLeft + selected.offsetWidth;
      const viewLeft = container.scrollLeft;
      const viewRight = viewLeft + container.clientWidth;
      const pad = 24;
      if (selectedLeft < viewLeft + pad) {
        container.scrollLeft = Math.max(0, selectedLeft - pad);
      } else if (selectedRight > viewRight - pad) {
        container.scrollLeft = selectedRight - container.clientWidth + pad;
      }
      this.updateThumbnailScroll();
    });
  }
  handleThumbnailScroll() {
    this.updateThumbnailScroll();
  }
  handleScrollbarKeyDown(event) {
    if (!this._thumbScrollContainer) {
      return;
    }
    const scrollRange = Math.max(0, this._thumbScrollContainer.scrollWidth - this._thumbScrollContainer.offsetWidth);
    const step = Math.max(1, this._thumbScrollContainer.offsetWidth / 4);
    let nextScrollLeft = this._thumbScrollContainer.scrollLeft;
    switch (event.key) {
      case 'ArrowLeft':
        nextScrollLeft -= step;
        break;
      case 'ArrowRight':
        nextScrollLeft += step;
        break;
      case 'PageUp':
        nextScrollLeft -= this._thumbScrollContainer.offsetWidth;
        break;
      case 'PageDown':
        nextScrollLeft += this._thumbScrollContainer.offsetWidth;
        break;
      case 'Home':
        nextScrollLeft = 0;
        break;
      case 'End':
        nextScrollLeft = scrollRange;
        break;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    this._thumbScrollContainer.scrollLeft = Math.max(0, Math.min(nextScrollLeft, scrollRange));
    this.updateThumbnailScroll();
  }
  startThumbScroll(event) {
    event.preventDefault();
    if (!this._thumbScrollbar || !this._thumbScrollbarThumb || !this._thumbScrollContainer) {
      return;
    }
    const trackRect = this._thumbScrollbar.getBoundingClientRect();
    const clickX = event.clientX - trackRect.left;
    const thumbWidth = this._thumbScrollbarThumb.offsetWidth;
    const maxThumbLeft = Math.max(0, this._thumbScrollbar.offsetWidth - thumbWidth);
    const currentLeft = this._thumbScrollbarThumb.offsetLeft;

    // Click on the track (outside the grabber) jumps there, then drag continues from that point
    let startLeft = currentLeft;
    if (clickX < currentLeft || clickX > currentLeft + thumbWidth) {
      startLeft = Math.max(0, Math.min(clickX - thumbWidth / 2, maxThumbLeft));
      this.applyScrollbarThumbLeft(startLeft);
    }
    this._isDraggingScrollbar = true;
    this._scrollbarStartX = event.clientX;
    this._scrollbarStartLeft = startLeft;
    this._thumbScrollbar.classList.add('is-dragging');
  }
  handleThumbDrag(event) {
    if (!this._isDraggingScrollbar) {
      return;
    }
    const deltaX = event.clientX - this._scrollbarStartX;
    const trackWidth = this._thumbScrollbar?.offsetWidth || 0;
    const thumbWidth = this._thumbScrollbarThumb?.offsetWidth || 0;
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const newThumbLeft = Math.max(0, Math.min(this._scrollbarStartLeft + deltaX, maxThumbLeft));
    this.applyScrollbarThumbLeft(newThumbLeft);
  }
  applyScrollbarThumbLeft(thumbLeft) {
    if (!this._thumbScrollbarThumb || !this._thumbScrollContainer) {
      return;
    }
    const trackWidth = this._thumbScrollbar?.offsetWidth || 0;
    const thumbWidth = this._thumbScrollbarThumb.offsetWidth;
    const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
    const scrollWidth = this._thumbScrollContainer.scrollWidth;
    const containerWidth = this._thumbScrollContainer.offsetWidth;
    const scrollRange = Math.max(0, scrollWidth - containerWidth);
    const ratio = maxThumbLeft > 0 ? thumbLeft / maxThumbLeft : 0;
    this._thumbScrollbarThumb.style.left = `${thumbLeft}px`;
    this._thumbScrollContainer.scrollLeft = ratio * scrollRange;
  }
  endThumbScroll() {
    this._isDraggingScrollbar = false;
    this._thumbScrollbar?.classList.remove('is-dragging');
  }
  /*LWC compiler v7.1.3*/
}
LightBoxV2.delegatesFocus = true;
_registerDecorators(LightBoxV2, {
  publicProps: {
    items: {
      config: 3
    },
    initialItemPosition: {
      config: 3
    },
    ga4EventType: {
      config: 0
    }
  },
  track: {
    _currentItem: 1,
    isVideoPlaying: 1,
    scrollbarValue: 1,
    scrollbarTabIndex: 1
  },
  wire: {
    i18nModule: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: () => i18nStore.getState().i18n
        };
      }
    },
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.listingDetails.carousel
        };
      }
    }
  },
  fields: ["_items", "_initialItemPosition", "_initialRender", "_isDraggingScrollbar", "_scrollbarStartX", "_scrollbarStartLeft", "_hiddenHeaderEls", "_raisedRootContainers", "_bodyStyleAttribute", "_bodyHadOpenClass"]
});
const __lwc_component_class_internal = _registerComponent(LightBoxV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;